import { ChangeDetectionStrategy, Component, computed, inject, signal, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ProformaRepository } from 'src/app/proforma/domain/proforma.repository';
import { DialogModule } from 'primeng/dialog';
import { TreeTableModule } from 'primeng/treetable';
import { ButtonModule } from 'primeng/button';
import { UtilService } from 'src/app/services/util.service';
import { TextareaModule } from 'primeng/textarea';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { StorageService } from 'src/app/services/storage.service';
import { ListadoClientePorEntidadParaAsignarDTO as Clientes, ListadoEntidadParaAsignarDTO, ParamListadoEntidades } from 'src/app/proforma/domain/response/proforma_response';
import { DropdownModule } from 'primeng/dropdown';
import { CardModule } from 'primeng/card';
import { TableModule } from 'primeng/table';
import { SelectModule } from 'primeng/select';
import { PopoverModule } from 'primeng/popover'; // Importar PopoverModule
import { Popover } from 'primeng/popover';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';


interface Grupo {
  grupo_id: number;
  nombre_grupo: string;
  expandido: boolean;
  clientes: Clientes[];
}
// Para usar en el tipo de las señales
type ItemAsignacion = Clientes | Grupo;

@Component({
  selector: 'app-asignacion-clientes',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    DialogModule,
    TreeTableModule,
    ButtonModule,
    FormsModule,
    TextareaModule,
    ProgressSpinnerModule,
    CardModule,
    DropdownModule,
    TableModule,
    SelectModule,
    PopoverModule,
    CommonModule
  ],
  templateUrl: './asignacion-clientes.component.html',
  styleUrl: './asignacion-clientes.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})

export class AsignacionClientesComponent {

  // Referencia al popover en la plantilla
  @ViewChild('op') op!: Popover;

  private fb = inject(FormBuilder);
  private asignacionService = inject(ProformaRepository);
  private util = inject(UtilService);
  private storage = inject(StorageService);
  private router = inject(Router);

  navegar(ruta: string) {
    this.router.navigate([ruta]);
  }

  loading = signal(false);
  showDialog = false;

  // === Sección de Ejecutivos ===

  ejecutivosSearchForm: FormGroup = this.fb.group({
    nombre: [''],
    estado: [null] // Valor por defecto 'Todos'
  });

  ejecutivosList = signal<ListadoEntidadParaAsignarDTO[]>([]);

  loadingEjecutivos = signal<boolean>(false);

  // === Sección de Customers ===

  customersSearchForm: FormGroup = this.fb.group({
    nombre: [''],
    estado: [null] // Valor por defecto 'Todos'
  });

  customersList = signal<ListadoEntidadParaAsignarDTO[]>([]);

  loadingCustomers = signal<boolean>(false);

  // Opciones para ambos selects (ejecutivos y customers)
  estadoOptions = [
    { label: 'Todos', value: null },
    { label: 'Asignados', value: 1 },
    { label: 'No asignados', value: 0 }
  ];

  constructor() {
    // Aquí podrías usar `onFormChanged` con `effect` si lo necesitaras, como en tu ejemplo.
    // Por ahora, la búsqueda se hará con un botón.
  }

  buscarListadoEntidades(tipo: number) {
    this.loadingEjecutivos.set(true);
    const params: ParamListadoEntidades = {
      tipo_entidad: tipo, // 3 para ejecutivos, 4 para customers
      asignados: this.ejecutivosSearchForm.get('estado')!.value,
      nombre: this.ejecutivosSearchForm.get('nombre')!.value.trim() || null
    };

    this.asignacionService.ListarEntidadesParaAsignar(params).subscribe({
      next: (response) => {
        if (response.datos.status === 200) {
          if( tipo === 3) {
            this.ejecutivosList.set(response.datos.result);
          } else if (tipo === 4) {
            this.customersList.set(response.datos.result);
          }else{
            console.warn('Tipo de entidad no reconocido');
            this.ejecutivosList.set([]);
            this.customersList.set([]);
          }
        } else {
          // Manejar otros estados, como 404
          this.ejecutivosList.set([]);
          this.customersList.set([]);
          console.warn(response.meta.mensaje);
        }
      },
      error: (err) => {
        console.error('Error al buscar ejecutivos:', err);
        this.ejecutivosList.set([]);
        this.customersList.set([]);
      },
      complete: () => {
        this.loadingEjecutivos.set(false);
      }
    });
  }

  // noAsignados = signal<ItemAsignacion[]>([]);
  // asignados = signal<ItemAsignacion[]>([]);

  // Signal para almacenar el ítem (cliente o grupo) actual del popover
  itemSeleccionadoParaPopover = signal<any>(null);

  // Las listas originales ahora son señales
  private _noAsignadosOriginal = signal<ItemAsignacion[]>([]);
  private _asignadosOriginal = signal<ItemAsignacion[]>([]);

  // Signals para los filtros de búsqueda
  filtroNoAsignadosTerm = signal<string>('');
  filtroAsignadosTerm = signal<string>('');

  // **Nuevas señales para los checkboxes de filtro por tipo**
  filtroNoAsignadosPorGrupo = signal<boolean>(true);
  filtroNoAsignadosPorCliente = signal<boolean>(true);
  filtroAsignadosPorGrupo = signal<boolean>(true);
  filtroAsignadosPorCliente = signal<boolean>(true);

  // Nuevas señales para los valores de los selects en la lista "No asignados"
  filtroTipoRegimen = signal<string>('001'); // Valor por defecto IMPORTACIONES
  filtroTipoTrafico = signal<string>('008'); // Valor por defecto TODOS LOS TRAFICOS

  // Nuevo filtro por ejecutivo
  filtroEjecutivoNoAsignados = signal<number | null>(null);

  // Opciones para los selects
  opcionesTipoRegimen = [
    { value: '001', label: 'IMPORTACIONES' },
    { value: '002', label: 'EXPORTACIONES' }
  ];

  opcionesTipoTrafico = [
    { value: '008', label: 'TODOS LOS TRAFICOS' },
    { value: '001', label: 'AFRICA' },
    { value: '002', label: 'ANTARTIDA' },
    { value: '003', label: 'ASIA' },
    { value: '004', label: 'EUROPA' },
    { value: '005', label: 'NORTE AMERICA' },
    { value: '006', label: 'OCEANIA' },
    { value: '007', label: 'SUR AMERICA' },
  ];

  onFiltroTipoRegimenChange(event: Event) {
    const selectElement = event.target as HTMLSelectElement;
    this.filtroTipoRegimen.set(selectElement.value);
  }
  onFiltroTipoTraficoChange(event: Event) {
    const selectElement = event.target as HTMLSelectElement;
    this.filtroTipoTrafico.set(selectElement.value);
  }

  // Método para mostrar el Popover con la información del ítem
  showInfoPopover(event: Event, item: any) {
    this.itemSeleccionadoParaPopover.set(item);
    this.op.show(event);
  }

  // Helper para obtener el label del régimen
  getLabelTipoRegimen(value: string | null | undefined): string {
    if (!value) return 'N/A';
    const opcion = this.opcionesTipoRegimen.find(opt => opt.value === value);
    return opcion ? opcion.label : 'N/A';
  }

  // Helper para obtener el label del tráfico
  getLabelTipoTrafico(value: string | null | undefined): string {
    if (!value) return 'N/A';
    const opcion = this.opcionesTipoTrafico.find(opt => opt.value === value);
    return opcion ? opcion.label : 'N/A';
  }

  // Propiedad computed para la lista de "No asignados"
  noAsignados = computed(() => {
    const searchTerm = this.filtroNoAsignadosTerm().toLowerCase();
    const showGroups = this.filtroNoAsignadosPorGrupo();
    const showClients = this.filtroNoAsignadosPorCliente();
    const originalList = this._noAsignadosOriginal();
    const ejecutivoSeleccionado = this.filtroEjecutivoNoAsignados();

    // Si no se seleccionó ningún tipo, mostrar todo por defecto
    if (!showGroups && !showClients) {
        return [];
    }

    // Filtrado por tipo de ítem
    let filteredByType = originalList.filter(item => {
        const isGroup = this.esGrupo(item);
        return (isGroup && showGroups) || (!isGroup && showClients);
    });

    // ✅ Filtrado por ejecutivo
    if (ejecutivoSeleccionado !== null) {
      filteredByType = filteredByType.filter(item => {
        if (this.esGrupo(item)) {
          // Grupo: al menos un cliente debe tener ese entC_Codigo
          return item.clientes.some(c => c.codigoEntidad === ejecutivoSeleccionado);
        } else {
          return item.codigoEntidad === ejecutivoSeleccionado;
        }
      });
    }

    // Filtrado por término de búsqueda
    if (searchTerm) {
      filteredByType = filteredByType.filter(item => {
        if (this.esGrupo(item)) {
          return item.nombre_grupo.toLowerCase().includes(searchTerm);
        } else {
          return (
            item.razon_social.toLowerCase().includes(searchTerm) ||
            item.entC_DocIden.toLowerCase().includes(searchTerm)
          );
        }
      });
    }
    return filteredByType;
  });

  // Propiedad computed para la lista de "Asignados"
  asignados = computed(() => {
    const searchTerm = this.filtroAsignadosTerm().toLowerCase();
    const showGroups = this.filtroAsignadosPorGrupo();
    const showClients = this.filtroAsignadosPorCliente();
    const originalList = this._asignadosOriginal();

    // Si no se seleccionó ningún tipo, mostrar todo por defecto
    if (!showGroups && !showClients) {
        return [];
    }

    // Filtrado por tipo de ítem
    let filteredByType = originalList.filter(item => {
        const isGroup = this.esGrupo(item);
        return (isGroup && showGroups) || (!isGroup && showClients);
    });

    // Filtrado por término de búsqueda
    if (searchTerm) {
        filteredByType = filteredByType.filter(item => {
            if (this.esGrupo(item)) {
                return item.nombre_grupo.toLowerCase().includes(searchTerm);
            } else {
                return item.razon_social.toLowerCase().includes(searchTerm) ||
                      item.entC_DocIden.toLowerCase().includes(searchTerm);
            }
        });
    }

    return filteredByType;
  });

  setCheckboxSignal(signal: any, event: Event) {
    const checked = (event.target as HTMLInputElement).checked;
    signal.set(checked);
  }

  // Señales para los campos de búsqueda
  filtroNoAsignados = signal<string>('');
  filtroAsignados = signal<string>('');

  loadingClientesParaAsignar = signal<boolean>(false);
  tipoEntidad = signal<number>(0); // 3 para ejecutivos, 4 para customers
  cod_entidad_selected = signal<number>(0); // Para almacenar el código de la entidad seleccionada

  tittleHeader = ""
  listaEjecutivosSelect = []

  abrirDialogAsignarEntidad(tipo: number, entidad: any){
    this.filtroNoAsignadosTerm.set("")
    this.filtroAsignadosTerm.set("")
    this.filtroEjecutivoNoAsignados.set(null)
    this.filtroNoAsignadosPorGrupo.set(true)
    this.filtroNoAsignadosPorCliente.set(true)
    this.filtroAsignadosPorGrupo.set(true)
    this.filtroAsignadosPorCliente.set(true)
    this.filtroTipoRegimen.set('001')
    this.filtroTipoTrafico.set('008')
    const codigo = entidad.entC_Codigo
    this.listaEjecutivosSelect = this.ejecutivosList().filter(e => e.entC_Codigo !== codigo)
    console.log("listaEjecutivosSelect", this.listaEjecutivosSelect)
    //colocar en el tittleHeader el título de "Asignar Clientes" + el this.tittleHeader.razon_social
    this.tittleHeader = "(EJECUTIVOS) Asignar Clientes a "+ entidad.razon_social
    this.tipoEntidad.set(tipo); // Guardar el tipo de entidad para usarlo en la asignación
    this.cod_entidad_selected.set(codigo); // Limpiar la entidad seleccionada antes de abrir el diálogo
    //1. abrir dialog
    this.showDialog = true;
    this.loadingClientesParaAsignar.set(true);
    //2. cargar clientes disponibles y asignados
    this.asignacionService.ListarClientesPorEntidadParaAsignar({
      tipo_entidad: tipo, // 3 para ejecutivos, 4 para customers
      entidad_id: codigo
    }).subscribe({
      next: (response) => {
        if (response.datos.status === 200) {
          this.organizarListas(response.datos.result || []);// vendrá el tipado de la interfaz Clientes
        } else {
          console.error('No se pudieron cargar los clientes para asignar.');
        }
      },
      error: (err) => {
        console.error('Error al cargar clientes para asignar:', err);
      },
      complete: () => {
        this.loadingClientesParaAsignar.set(false);
      }
      });
  }

  listaCustomerSelect = []
  showDialogCustomer = false;

  private _noAsignadosOriginalCustomer = signal([])
  private _asignadosOriginalCustomer = signal([])

  filtroNoAsignadosEjecutivosTerm = signal<string>('');
  filtroAsignadosEjecutivosTerm = signal<string>('');

  // Nuevo filtro por ejecutivo
  filtroCustomerNoAsignados = signal<number | null>(null);

  asignadosEjecutivos = computed(() => {
    const searchTerm = this.filtroAsignadosEjecutivosTerm().toLowerCase();
    const originalList = this._asignadosOriginalCustomer();

    // Filtrado por tipo de ítem
    let filteredByType = originalList;

    // Filtrado por término de búsqueda
    if (searchTerm) {
      filteredByType = filteredByType.filter(item => {
          return item.nombre_ejecutivo.toLowerCase().includes(searchTerm)
      });
    }

    return filteredByType;
  });

  noAsignadosEjecutivos = computed(() => {
    const searchTerm = this.filtroNoAsignadosEjecutivosTerm().toLowerCase();
    const originalList = this._noAsignadosOriginalCustomer();
    const customerSeleccionado = this.filtroCustomerNoAsignados();

    // Filtrado por tipo de ítem
    let filteredByType = originalList;

    // ✅ Filtrado por ejecutivo
    if (customerSeleccionado !== null) {
      filteredByType = filteredByType.filter(item => {
        return item.entc_customer === customerSeleccionado;
      });
    }

    // Filtrado por término de búsqueda
    if (searchTerm) {
      filteredByType = filteredByType.filter(item => {
        return item.nombre_ejecutivo.toLowerCase().includes(searchTerm)
      });
    }

    return filteredByType;
  });

  // **Nuevas funciones de mover actualizadas**
  moverAAsignadosEjecutivos(item: any) {
    // Si el item estaba en "No asignado" y lo agrego → marcar como agregado
    if (item.estado_relacion === "No asignado") {
      item.cambioEstado = "agregado"; // para pintar verde
    } else {
      item.cambioEstado = null; // no se pinta si no corresponde
    }

    // Quitar de no asignados
    this._noAsignadosOriginalCustomer.update(lista => lista.filter(i => i !== item));

    // Agregar a asignados
    this._asignadosOriginalCustomer.update(lista => [item, ...lista]);
  }

  moverANoAsignadosEjecutivos(item: any) {
    // Si el item estaba originalmente "Asignado" → marcar como removido
    if (item.estado_relacion === "Asignado") {
      item.cambioEstado = "removido"; // para pintar rojo
    } else {
      item.cambioEstado = null; // si era agregado y lo quito, no se pinta
    }

    // Quitar de asignados
    this._asignadosOriginalCustomer.update(lista => lista.filter(i => i !== item));

    // Agregar a no asignados
    this._noAsignadosOriginalCustomer.update(lista => [item, ...lista]);
  }
  tittleHeaderCustomer: string = ""
  abrirDialogAsignarCustomer(entidad:any){
    this.filtroAsignadosEjecutivosTerm.set("")
    this.filtroNoAsignadosEjecutivosTerm.set("")
    this.filtroCustomerNoAsignados.set(null)
    this.tipoEntidad.set(4);
    const codigo = entidad.entC_Codigo
    this.listaCustomerSelect = this.customersList().filter(e => e.entC_Codigo !== codigo)
    this.cod_entidad_selected.set(codigo);

    //1. abrir dialog
    this.showDialogCustomer = true;
    this.loadingClientesParaAsignar.set(true);
    this.tittleHeaderCustomer = "(CUSTOMER) Asignar Ejecutivos a "+ entidad.razon_social
    this.asignacionService.ObtenerRelacionadosPorCustomer({
      entc_customer: codigo,
    }).subscribe({
      next: (response) => {
        if (response.datos.status === 200) {
          this.organizarListasCustomer(response.datos.result || []);// vendrá el tipado de la interfaz Clientes
        } else {
          console.error('No se pudieron cargar los clientes para asignar.');
        }
      },
      error: (err) => {
        console.error('Error al cargar clientes para asignar:', err);
      },
      complete: () => {
        this.loadingClientesParaAsignar.set(false);
      }
      });
  }

  organizarListasCustomer(data: any[]){
    const separar = (tipo: string) => {
      const filtrados = data.filter(c => c.estado_relacion === tipo);

      return filtrados;
    };
    this._noAsignadosOriginalCustomer.set(separar('No asignado'));
    this._asignadosOriginalCustomer.set(separar('Asignado'));
  }

  organizarListas(data: Clientes[]) {
    const separar = (tipo: string) => {
      // 1. Clonar y ordenar la data original
      const filtrados = [...data].filter(c => c.tipoRegistro === tipo);
      filtrados.sort((a, b) => {
          const grupoA = a.grupo_id ?? 999999;
          const grupoB = b.grupo_id ?? 999999;
          if (grupoA !== grupoB) {
              return grupoA - grupoB;
          }
          const nombreA = a.nombre_grupo ?? a.razon_social;
          const nombreB = b.nombre_grupo ?? b.razon_social;
          return nombreA.localeCompare(nombreB);
      });

      // 2. Crear las listas de grupos y clientes sin grupo
      const listaFinal: ItemAsignacion[] = [];
      const gruposMap = new Map<number, Clientes[]>();
      const clientesSinGrupo: Clientes[] = [];

      filtrados.forEach(c => {
        if (c.grupo_id && c.nombre_grupo) {
          if (!gruposMap.has(c.grupo_id)) {
            gruposMap.set(c.grupo_id, []);
          }
          gruposMap.get(c.grupo_id)!.push(c);
        } else {
          clientesSinGrupo.push(c);
        }
      });

      // 3. Convertir el mapa de grupos en una lista y ordenarla
      const gruposList = Array.from(gruposMap.values()).map(clientes => {
          return {
              grupo_id: clientes[0].grupo_id!,
              nombre_grupo: clientes[0].nombre_grupo!,
              expandido: false,
              clientes
          };
      });

      // Modificación aquí para ordenar los grupos
      gruposList.sort((a, b) => {
          const regex = /^grupo_(\d+)$/i; // Expresión regular para "grupo_" seguido de números
          const matchA = a.nombre_grupo.match(regex);
          const matchB = b.nombre_grupo.match(regex);

          // Si ambos nombres cumplen el patrón numérico
          if (matchA && matchB) {
              const numA = parseInt(matchA[1], 10);
              const numB = parseInt(matchB[1], 10);
              return numA - numB;
          }
          // Si solo el primer nombre cumple el patrón, va después
          if (matchA) {
              return 1;
          }
          // Si solo el segundo nombre cumple el patrón, va después
          if (matchB) {
              return -1;
          }
          // Si ninguno cumple el patrón, se ordena alfabéticamente
          return a.nombre_grupo.localeCompare(b.nombre_grupo);
      });

      // 4. Concatenar las listas
      listaFinal.push(...gruposList);
      clientesSinGrupo.sort((a, b) => a.razon_social.localeCompare(b.razon_social));
      listaFinal.push(...clientesSinGrupo);

      return listaFinal;
    };

    this._noAsignadosOriginal.set(separar('No asignado'));
    this._asignadosOriginal.set(separar('Asignado'));
  }

  toggleGrupo(item: ItemAsignacion) {
    if (this.esGrupo(item)) {
      item.expandido = !item.expandido;
    }
  }

  // **Nuevas funciones de mover actualizadas**
  moverAAsignados(item: ItemAsignacion) {
    const regimen = this.filtroTipoRegimen();
    const trafico = this.filtroTipoTrafico();

    // Actualizar el ítem con los valores de los selects antes de mover
    if (this.esGrupo(item)) {
      // Si es un grupo, actualizar cada cliente dentro del grupo
      item.clientes = item.clientes.map(cliente => ({
        ...cliente,
        conS_CodRGM: regimen,
        tipO_CodTRF: trafico
      }));
    } else {
      // Si es un cliente simple, actualizarlo directamente
      item.conS_CodRGM = regimen;
      item.tipO_CodTRF = trafico;
    }

    // Actualizar la lista de no asignados (quitar el ítem)
    this._noAsignadosOriginal.update(lista => {
      const newList = [...lista];
      const index = newList.indexOf(item);
      if (index > -1) {
        newList.splice(index, 1);
      }
      return newList;
    });

    // Actualizar la lista de asignados (agregar el ítem)
    this._asignadosOriginal.update(lista => [item, ...lista]);
  }

  moverTodosAAsignados() {
    const itemsFiltrados = this.noAsignados();

    // Reutilizamos la lógica existente
    itemsFiltrados.forEach(item => {
      this.moverAAsignados(item);
    });
  }

  moverANoAsignados(item: ItemAsignacion) {
    // Actualizar el ítem para colocar los valores en null antes de mover
    if (this.esGrupo(item)) {
      // Si es un grupo, actualizar cada cliente dentro del grupo
      item.clientes = item.clientes.map(cliente => ({
        ...cliente,
        conS_CodRGM: null,
        tipO_CodTRF: null
      }));
    } else {
      // Si es un cliente simple, actualizarlo directamente
      item.conS_CodRGM = null;
      item.tipO_CodTRF = null;
      // item.tipoRegistro='Eliminado'
    }

    // Actualizar la lista de asignados (quitar el ítem)
    this._asignadosOriginal.update(lista => {
      const newList = [...lista];
      const index = newList.indexOf(item);
      if (index > -1) {
        newList.splice(index, 1);
      }
      return newList;
    });

    // Actualizar la lista de no asignados (agregar el ítem)
    this._noAsignadosOriginal.update(lista => [item, ...lista]);
  }

  moverTodosANoAsignados() {
    const itemsFiltrados = this.asignados();

    // Solo tomamos los "recientes" (tipoRegistro === 'Asignado')
    const recientes = itemsFiltrados.filter(item => {
      if (this.esGrupo(item)) {
        return item.clientes.length > 0 && item.clientes[0].tipoRegistro !== "Asignado";
      } else {
        return item.tipoRegistro !== "Asignado";
      }
    });

    // Reutilizamos la misma lógica que ya tienes
    recientes.forEach(item => this.moverANoAsignados(item));
  }

  hasRecentAssigned(): boolean {
    return this.asignados().some(item =>
      this.esGrupo(item) ? item.clientes[0]?.tipoRegistro !== 'Asignado' : item.tipoRegistro !== 'Asignado'
    );
  }

  // Función para verificar si un ítem es un Grupo
  esGrupo(item: ItemAsignacion): item is Grupo {
    // La verificación por la existencia de la propiedad 'clientes' es más fiable.
    return (item as Grupo).clientes !== undefined;
  }

  // Función de trackBy actualizada
  getTrackByValue(item: ItemAsignacion): number | string {
    if (this.esGrupo(item)) {
      return item.grupo_id;
    } else {
      return item.entC_Codigo;
    }
  }
  getTrackByValueCustomer(item: any): number {
    return item.entC_Codigo;
  }



  getCantidadAgregada(): number {
    const clientesAsignados = this.asignados();
    const clientesParaEnviar = clientesAsignados.flatMap(item => {
      if (this.esGrupo(item)) {
        // Si es un grupo, devolver su array de clientes
        return item.clientes;
      } else {
        // Si es un cliente, devolverlo en un array para que flatMap lo maneje correctamente
        return [item];
      }
    });

    // const nuevosClientesAsignados = clientesParaEnviar.filter(
    //   (cliente: any) => cliente.tipoRegistro === 'No asignado'
    // );
    //! se cambio a clienteParaEnviar
    const nuevosClientesAsignados = clientesParaEnviar.filter(
      (cliente: any) => cliente.tipoRegistro === 'Asignado'
    );

    return nuevosClientesAsignados.length;
  }

  getCantidadQuitada(): number {
    const clientesNoAsignados = this.noAsignados();
    const clientesParaEnviar = clientesNoAsignados.flatMap(item => {
      if (this.esGrupo(item)) {
        // Si es un grupo, devolver su array de clientes
        return item.clientes;
      } else {
        // Si es un cliente, devolverlo en un array para que flatMap lo maneje correctamente
        return [item];
      }
    });

    // const nuevosClientesAsignados = clientesParaEnviar.filter(
    //   (cliente: any) => cliente.tipoRegistro === 'No asignado'
    // );
    //! se cambio a clienteParaEnviar
    const nuevosClientesAsignados = clientesParaEnviar.filter(
      (cliente: any) => cliente.tipoRegistro === 'No Asignado'
    );

    return nuevosClientesAsignados.length;
  }

  cancelar (){
     this.showDialog = false; // Cerrar el diálogo
    // Aquí podrías recargar las listas o hacer otra acción necesaria
    this.buscarListadoEntidades(this.tipoEntidad());
    this._asignadosOriginal.set([]);
    this._noAsignadosOriginal.set([]);
  }
  cerrar (){
    this.showDialogCustomer = false; // Cerrar el diálogo
    this._asignadosOriginalCustomer.set([]);
    this._noAsignadosOriginalCustomer.set([]);
  }

  guardarAsignacion() {

    const clientesAsignados = this.asignados();
    const clientesNoAsignados = this.noAsignados();

    const clientesParaEnviar = clientesAsignados.flatMap(item => {
      if (this.esGrupo(item)) {
        // Si es un grupo, devolver su array de clientes
        return item.clientes;
      } else {
        // Si es un cliente, devolverlo en un array para que flatMap lo maneje correctamente
        return [item];
      }
    });
    const clientesParaEnviar2 = clientesNoAsignados.flatMap(item => {
      if (this.esGrupo(item)) {
        // Si es un grupo, devolver su array de clientes
        return item.clientes;
      } else {
        // Si es un cliente, devolverlo en un array para que flatMap lo maneje correctamente
        return [item];
      }
    });

    const nuevosClientesAsignados1 = clientesParaEnviar.filter(
      (cliente: any) => cliente.tipoRegistro === 'No asignado'
    );

    const nuevosClientesAsignados2 = clientesParaEnviar2.filter(
      (cliente: any) => cliente.tipoRegistro === "Asignado"//'Asignado'
    );

    const nuevosClientesAsignados = [...nuevosClientesAsignados1, ...nuevosClientesAsignados2]

    const datosParaServicio = nuevosClientesAsignados.map(item => ({
      param_cliente_id: item.entC_Codigo,
      param_entidad_id: this.cod_entidad_selected() || 0,
      cod_rgm: item.conS_CodRGM || null,
      cod_trf: item.tipO_CodTRF || null,
      param_relaTipos: this.tipoEntidad() === 3 ? 'VEN' : 'CSE',
      tipo_entidad: this.tipoEntidad() || 0,
      tipo_registro : item.tipoRegistro
    }));


    const loadingScreen = this.util.showLoadingScreen({ message: "Guardando datos ..." });

    //SOLO AGREGAR CLIENTES NO ASIGNADOS, es decir los que tienen el tipoRegistro 'No asignado'
    this.asignacionService.AsignarClientesPorEntidad({
      param_usrCrea: this.storage.get('codusuario'),
      datos: datosParaServicio,
    }).subscribe({
      next: (response) => {
        if (response.datos.status === 200) {
          // this.util.showSuccess('Asignación guardada correctamente');
          this.util.ShowMessage({
            titulo: 'Éxito',
            mensaje: 'Asignación guardada correctamente',
            tipo: 'ok',
            autocerrar: true,
            tiempo: 4000
          })
          this.showDialog = false; // Cerrar el diálogo
          // Aquí podrías recargar las listas o hacer otra acción necesaria
          this.buscarListadoEntidades(this.tipoEntidad());
        //  this._asignadosOriginal.set([]);
        //  this._noAsignadosOriginal.set([]);

        }else{

          this.util.ShowMessage({
            titulo: 'Error',
            mensaje: response.meta.mensaje || 'Error desconocido.',
            tipo: 'error',
            autocerrar: true,
            tiempo: 4000
          })
          loadingScreen.close();
        }
      }
      , error: (err) => {
        this.util.ShowMessage({
          titulo: 'Error',
          mensaje: 'Error al guardar la asignación',
          detalle: err.message || 'Error desconocido.',
          tipo: 'error',
          autocerrar: true,
          tiempo: 4000
        })
        loadingScreen.close();
      }
      , complete: () => {
        loadingScreen.close();
      }
    });
  }

  guardarAsignacionCustomer(){
    const ejecutivosQuitados = this.noAsignadosEjecutivos().filter(
      (ejecutivo: any) => ejecutivo.estado_relacion === "Asignado"// Estos son los que se han quitado(puede que no haya ninguno)
    );
    const ejecutivosAgregados = this.asignadosEjecutivos().filter(
      (ejecutivo: any) => ejecutivo.estado_relacion === "No asignado"//Estos son los que se han agregado(puede que no haya ninguno)
    );
    const listadoCompleto = [...ejecutivosQuitados, ...ejecutivosAgregados]

    const payload = {
      entc_customer: this.cod_entidad_selected() || 0,
      ejecutivos: listadoCompleto.map(e => ({
        entc_ejecutivo: e.entc_ejecutivo,
        estado_relacion: e.estado_relacion
      })),
      param_usrCrea: this.storage.get('codusuario')
    };

    const loadingScreen = this.util.showLoadingScreen({ message: "Guardando datos ..." });
    this.asignacionService.AsignarEjecutivosPorCustomer(payload).subscribe({
      next: (response) => {
        if (response.datos.status === 200) {
          // this.util.showSuccess('Asignación guardada correctamente');
          this.util.ShowMessage({
            titulo: 'Éxito',
            mensaje: 'Asignación guardada correctamente',
            tipo: 'ok',
            autocerrar: true,
            tiempo: 4000
          })
          this.showDialogCustomer = false; // Cerrar el diálogo
          // Aquí podrías recargar las listas o hacer otra acción necesaria
          this.buscarListadoEntidades(this.tipoEntidad());
        //  this._asignadosOriginal.set([]);
        //  this._noAsignadosOriginal.set([]);

        }else{

          this.util.ShowMessage({
            titulo: 'Error',
            mensaje: response.meta.mensaje || 'Error desconocido.',
            tipo: 'error',
            autocerrar: true,
            tiempo: 4000
          })
          loadingScreen.close();
        }
      }
      , error: (err) => {
        this.util.ShowMessage({
          titulo: 'Error',
          mensaje: 'Error al guardar la asignación',
          detalle: err.message || 'Error desconocido.',
          tipo: 'error',
          autocerrar: true,
          tiempo: 4000
        })
        loadingScreen.close();
      }
      , complete: () => {
        loadingScreen.close();
      }
    });
  }
}
